import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import type { CreditRangeEnum } from '@cc-shopping/contracts/credit-cards-page/roundup/utils';
import { getSortedRows } from 'src/features/summary-table/utils';
import { FilterOffersByType, SortOffersByType } from '../../enums';

export const getFilteredOffers = <T extends { id: string }>({
  allOffers,
  creditRangeFilter,
  issuerFilter,
  preComputedFilterOfferIds,
  cardFeatureFilter,
}: {
  allOffers: T[];
  cardFeatureFilter: FilterOffersByType;
  issuerFilter: string[];
  creditRangeFilter: CreditRangeEnum[];
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
}) => {
  let eligibleOfferIds: Set<string> = new Set();

  switch (cardFeatureFilter) {
    case FilterOffersByType.ALL_CARDS:
      eligibleOfferIds = preComputedFilterOfferIds.allOfferIds;
      break;
    case FilterOffersByType.HAS_INTRO_OFFER:
      eligibleOfferIds = preComputedFilterOfferIds.introOfferOfferIds;
      break;
    case FilterOffersByType.NO_ANNUAL_FEE:
      eligibleOfferIds = preComputedFilterOfferIds.noAnnualFeeOfferIds;
      break;
  }

  // Filter by Issuers
  if (issuerFilter.length > 0) {
    let allInstitutionOfferIds = new Set<string>();
    for (const institutionId of issuerFilter) {
      const safeCorrespondingSet = preComputedFilterOfferIds.offersByIssuerMap[institutionId] ?? [];
      allInstitutionOfferIds = new Set([...allInstitutionOfferIds, ...safeCorrespondingSet]);
    }
    eligibleOfferIds = new Set(
      [...eligibleOfferIds].filter(offerId => allInstitutionOfferIds.has(offerId)),
    );
  }

  // Filter by credit range
  if (creditRangeFilter.length > 0) {
    let allCreditRangeOfferIds = new Set<string>();
    for (const range of creditRangeFilter) {
      const safeCorrespondingSet = preComputedFilterOfferIds.offersByRCSMap[range] ?? [];
      allCreditRangeOfferIds = new Set([...allCreditRangeOfferIds, ...safeCorrespondingSet]);
    }
    eligibleOfferIds = new Set(
      [...eligibleOfferIds].filter(offerId => allCreditRangeOfferIds.has(offerId)),
    );
  }

  return allOffers.filter(offer => eligibleOfferIds.has(offer.id));
};

export const getSortedOffers = <T extends { id: string }>({
  filteredOffers,
  preComputedFilterOfferIds,
  featuredSequence,
  sortBy,
}: {
  filteredOffers: T[];
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
  featuredSequence: string[];
  sortBy: SortOffersByType;
}) => {
  let sequence;
  switch (sortBy) {
    case SortOffersByType.FEATURED:
      sequence = featuredSequence;
      break;
    case SortOffersByType.HIGHEST_RATING:
      sequence = preComputedFilterOfferIds.sortedByHighestRating;
      break;
    case SortOffersByType.LOWEST_ANNUAL_FEE:
      sequence = preComputedFilterOfferIds.sortedByLowestAnnualFee;
      break;
    case SortOffersByType.LOWEST_APR:
      sequence = preComputedFilterOfferIds.sortedByLowestAPR;
      break;
  }

  return getSortedRows(filteredOffers, sequence);
};
